import React from 'react';
import {
  Edit,
  Create,
  SimpleForm,
  ReferenceInput,
  SelectInput,
  BooleanInput,
  DateInput,
  TextInput,
  Show,
  SimpleShowLayout,
  ReferenceField,
  BooleanField,
  DateField,
  TextField,
  FunctionField,
  FormDataConsumer,
  useRecordContext,
  useGetOne,
  NumberInput,
  NumberField,
} from 'react-admin';
import { Box, Typography, Alert } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';

const ColorPickerField = () => {
  const form = useFormContext();
  const color = form.watch('color') || '#1976d2';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 1 }}>
      <Typography variant="body2" color="text.secondary">Kolor na osi czasu:</Typography>
      <input
        type="color"
        value={color}
        onChange={e => form.setValue('color', e.target.value, { shouldDirty: true })}
        style={{ width: 36, height: 36, padding: 2, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
      />
    </Box>
  );
};

const DEFAULT_GUEST_COUNTS = [2, 3, 4];

const formatPriceValue = (value) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }
  return numericValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const validateApartmentPrice = (values) => {
  const errors = {};

  if (!values.apartment) {
    errors.apartment = 'Required';
  }

  if (values.is_base) {
    if (values.start_date) {
      errors.start_date = 'Base pricing cannot have a start date';
    }
    if (values.end_date) {
      errors.end_date = 'Base pricing cannot have an end date';
    }
  } else {
    if (!values.start_date) {
      errors.start_date = 'Required';
    }
    if (!values.end_date) {
      errors.end_date = 'Required';
    }

    if (values.start_date && values.end_date) {
      const start = new Date(values.start_date);
      const end = new Date(values.end_date);
      if (start > end) {
        errors.end_date = 'End date must be on or after start date';
      }
    }
  }

  Object.entries(values)
    .filter(([key]) => key.startsWith('price_'))
    .forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) {
        errors[key] = 'Required';
      } else if (Number(value) < 0) {
        errors[key] = 'Must be greater or equal to 0';
      }
    });

  return errors;
};

const transformApartmentPrice = (data, { previousData } = {}) => {
  const cleanedData = {
    ...previousData,
    ...data,
  };
  const cleanedPrices = {};

  Object.entries(cleanedData).forEach(([key, value]) => {
    const match = key.match(/^price_(\d+)$/);
    if (!match) {
      return;
    }

    delete cleanedData[key];

    if (value === '' || value === null || value === undefined) {
      return;
    }

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue)) {
      cleanedPrices[match[1]] = numericValue;
    }
  });

  cleanedData.start_date = cleanedData.is_base ? null : cleanedData.start_date || null;
  cleanedData.end_date = cleanedData.is_base ? null : cleanedData.end_date || null;
  if (!cleanedData.apartment && previousData?.apartment) {
    cleanedData.apartment = previousData.apartment;
  }
  const allowedCounts = typeof cleanedData.__guestCounts === 'string'
    ? cleanedData.__guestCounts.split(',').filter(Boolean)
    : null;
  if (allowedCounts) {
    const allowedSet = new Set(allowedCounts);
    Object.keys(cleanedPrices).forEach((count) => {
      if (!allowedSet.has(count)) {
        delete cleanedPrices[count];
      }
    });
  }
  cleanedData.prices = cleanedPrices;
  cleanedData.notes = cleanedData.notes || '';
  delete cleanedData.id;
  delete cleanedData.created_at;
  delete cleanedData.updated_at;
  delete cleanedData.__guestCounts;

  return cleanedData;
};

const useSupportedGuestCounts = (apartmentId) => {
  const { data } = useGetOne(
    'apartments',
    { id: apartmentId },
    {
      enabled: Boolean(apartmentId),
    },
  );

  if (!apartmentId) {
    return { counts: [], isFallback: false };
  }

  const counts = Array.isArray(data?.supported_guest_counts)
    ? data.supported_guest_counts
    : [];

  if (!counts.length) {
    return { counts: DEFAULT_GUEST_COUNTS, isFallback: true };
  }

  const sanitized = [...new Set(counts.map((count) => Number(count)))]
    .filter((count) => !Number.isNaN(count) && count > 0)
    .sort((a, b) => a - b);

  return { counts: sanitized, isFallback: false };
};

const GuestPriceInputs = ({ counts }) => {
  if (!counts.length) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Select an apartment to load supported guest counts.
      </Alert>
    );
  }

  return (
    <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={2} mb={2}>
      {counts.map((count) => (
        <NumberInput
          key={count}
          source={`price_${count}`}
          label={`Price for ${count} guest${count > 1 ? 's' : ''}`}
          min={0}
          step={1}
          fullWidth
          required
        />
      ))}
    </Box>
  );
};

const GuestPricingFields = ({ apartmentId }) => {
  const { counts, isFallback } = useSupportedGuestCounts(apartmentId);
  const form = useFormContext();

  React.useEffect(() => {
    if (!form) {
      return;
    }

    form.register('__guestCounts');
  }, [form]);

  React.useEffect(() => {
    if (!form) {
      return;
    }

    const allowedSet = new Set(counts);
    const values = form.getValues();

    Object.keys(values)
      .filter((key) => key.startsWith('price_'))
      .forEach((key) => {
        const count = Number(key.split('_')[1]);
        if (!allowedSet.has(count)) {
          const currentValue = form.getValues(key);
          if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
            form.setValue(key, undefined, { shouldDirty: true, shouldValidate: false });
          }
        }
      });

    const previous = form.getValues('__guestCounts');
    const next = counts.join(',');
    if (previous !== next) {
      form.setValue('__guestCounts', next, { shouldDirty: false, shouldValidate: false });
    }
  }, [counts, form]);

  return (
    <>
      {isFallback && apartmentId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This apartment has no custom guest counts configured. Using defaults ({DEFAULT_GUEST_COUNTS.join(', ')} guests).
        </Alert>
      )}
      <GuestPriceInputs counts={counts} />
    </>
  );
};

const ApartmentPriceForm = (props) => {
  const { redirect: redirectOverride, defaultValues: incomingDefaults = {}, ...rest } = props;
  const record = useRecordContext();
  const redirectTarget = redirectOverride ?? (record?.apartment ? `/apartments/${record.apartment}` : false);

  const mergedDefaultValues = React.useMemo(() => {
    const base = { ...incomingDefaults };

    if (record) {
      Object.assign(base, record);
      if (record.prices) {
        Object.entries(record.prices).forEach(([count, value]) => {
          base[`price_${count}`] = value;
        });
      }
    }

    delete base.prices;
    return base;
  }, [incomingDefaults, record]);

  return (
    <SimpleForm
      {...rest}
      warnWhenUnsavedChanges
      validate={validateApartmentPrice}
      redirect={redirectTarget}
      defaultValues={mergedDefaultValues}
    >
      <ReferenceInput source="apartment" reference="apartments" required>
        <SelectInput optionText="name" label="Apartment" disabled={Boolean(record?.id)} />
      </ReferenceInput>
      <BooleanInput source="is_base" label="Base pricing" />

      <FormDataConsumer>
        {({ formData }) => (
          <>
            <DateInput
              source="start_date"
              label="Start date"
              disabled={formData.is_base}
            />
            <DateInput
              source="end_date"
              label="End date"
              disabled={formData.is_base}
            />
            <GuestPricingFields apartmentId={formData.apartment} />
          </>
        )}
      </FormDataConsumer>

      <TextInput source="notes" label="Notes" fullWidth />
      <TextInput source="name" label="Nazwa sezonu" fullWidth helperText="Np. Wielkanoc, Sezon letni (opcjonalne)" />
      <NumberInput source="min_nights" label="Min. nocy" min={1} step={1} helperText="Minimalna liczba nocy (opcjonalne)" />
      <NumberInput source="priority" label="Priorytet (1-10)" min={1} max={10} step={1} defaultValue={1} helperText="Wyższy priorytet wygrywa przy nakładaniu się okresów: 1=sezonowe, 5=ferie, 10=premium" />
      <ColorPickerField />
    </SimpleForm>
  );
};

export const ApartmentPriceCreate = (props) => {
  const location = useLocation();
  const defaultValues = location.state?.record ? { ...location.state.record } : {};
  const redirectTo = defaultValues.apartment ? `/apartments/${defaultValues.apartment}` : false;

  return (
    <Create {...props} transform={transformApartmentPrice}>
      <ApartmentPriceForm defaultValues={defaultValues} redirect={redirectTo} />
    </Create>
  );
};

export const ApartmentPriceEdit = (props) => (
  <Edit {...props} transform={transformApartmentPrice}>
    <ApartmentPriceForm />
  </Edit>
);

export const ApartmentPriceListRedirect = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    navigate('/apartments', { replace: true });
  }, [navigate]);

  return (
    <Box p={2}>
      <Typography variant="body2" color="text.secondary">
        Pricing is managed from within each apartment. Redirecting you to the apartment list…
      </Typography>
    </Box>
  );
};

const formatPrices = (prices) => {
  if (!prices || typeof prices !== 'object') {
    return '—';
  }

  return Object.entries(prices)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([count, value]) => `${count} guests: ${formatPriceValue(value)}`)
    .join(', ');
};

export const ApartmentPriceShow = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <ReferenceField source="apartment" reference="apartments" label="Apartment">
        <TextField source="name" />
      </ReferenceField>
      <BooleanField source="is_base" label="Base pricing" />
      <DateField source="start_date" label="Start date" />
      <DateField source="end_date" label="End date" />
      <FunctionField label="Prices" render={(record) => formatPrices(record.prices)} />
      <TextField source="notes" label="Notes" />
      <TextField source="name" label="Nazwa sezonu" />
      <NumberField source="min_nights" label="Min. nocy" />
      <NumberField source="priority" label="Priorytet" />
      <TextField source="color" label="Kolor" />
    </SimpleShowLayout>
  </Show>
);
